import React, { useContext } from "react";
import { PhotoManagerContext } from "..";
import { LibraryFoldersViewProps, Folder, EnhancedFolder } from "../types";
const folderIcon = require("../../styles/images/folder-icon.svg");
const shortCutIcon = require("../../styles/images/folder-shortcut.svg");
const editIcon = require("../../styles/images/edit.svg");
const submitIcon = require("../../../ManagePrepaidCustomers/components/images/submit.svg");
const cancelIcon = require("../../../ManagePrepaidCustomers/components/images/cancel.svg");
import FolderActions from "../FolderActions";
import LoadingSpinner from "../PhotoGridBreadcrumb/LoadingSpinner";
import {
  findEntityPath,
  getEncodedEntityPath
} from "../FolderFeatures/EncryptionOfFolder";
import { useEditEntityNameInGrid } from "../useEditEntityNameInGrid";
import { updateFolder } from "../../API/api";

const LibraryFoldersView: React.FC<LibraryFoldersViewProps> = ({
  libraryFolders,
  onLibraryFolderClick,
  onClickCreateLibraryFolder
}) => {
  const context = useContext(PhotoManagerContext);

  const {
    entitiesInEditMode,
    entityNameDrafts,
    entitiesInLoadingState,
    toggleEditMode,
    updateDraftName,
    handleKeyDownAction,
    submitNameChange
  } = useEditEntityNameInGrid<Folder>({
    updateAPI: async (folder: Folder, newName: string) => {
      await updateFolder(folder.id, newName, folder.parentFolderId);
    },
    onSuccess: (folder: Folder, newName: string) => {
      context?.reloadFolderData("root", folder.id, null);
    }
  });

  const handleFolderNameClick = (folder: EnhancedFolder) => {
    const pathTrail = findEntityPath(
      context?.libraryRootFolders || [],
      folder.referenceId || folder.id,
      "folder"
    );
    if (pathTrail) {
      const encodedPath = getEncodedEntityPath(pathTrail);
      context?.setEncodedString(encodedPath);
      onLibraryFolderClick(folder, encodedPath);
    }
  };

  return (
    <div>
      <div className="ws-folders-section">
        <div className="ws-library-view-heading folders-heading">Folders</div>
        <div className="ws-folders-grid">
          {libraryFolders.map((folder: Folder) => (
            <div
              key={folder.id}
              className="ws-folder-card"
              onClick={() => {
                handleFolderNameClick(folder);
              }}
            >
              {folder.isReference ? (
                <img
                  src={shortCutIcon}
                  alt="short-cut"
                  className="shortcut-icon"
                />
              ) : null}
              <div className="folder-content">
                <div className="folder-left-content">
                  <div className="folder-icon-div">
                    <img
                      src={folderIcon}
                      alt="folder-icon"
                      className="folder-icon"
                    />
                  </div>
                  <div className="folder-details">
                    {entitiesInEditMode.includes(folder.id) ? (
                      <div
                        className="ws-edit-album-name"
                        data-edit-entity-id={folder.id}
                        onClick={e => e.stopPropagation()}
                      >
                        <textarea
                          defaultValue={
                            entityNameDrafts[folder.id] || folder.name
                          }
                          onChange={e =>
                            updateDraftName(e, folder.id, e.target.value)
                          }
                          onKeyDown={e => handleKeyDownAction(e, folder)}
                          onClick={e => e.stopPropagation()}
                          autoFocus
                          onFocus={e => {
                            const val = e.target.value;
                            e.target.value = "";
                            e.target.value = val;
                          }}
                        />
                        <div className="ws-edit-folder-actions">
                          {entitiesInLoadingState.includes(folder.id) ? (
                            <span
                              className="fa fa-spinner fa-spin"
                              style={{ marginRight: 5 }}
                            ></span>
                          ) : (
                            <>
                              <img
                                className="actionIcon"
                                src={submitIcon}
                                onClick={e => submitNameChange(e, folder)}
                                style={{ cursor: "pointer", opacity: 1 }}
                              />
                              <img
                                className="actionIcon"
                                style={{
                                  marginLeft: 5,
                                  cursor: "pointer",
                                  opacity: 1
                                }}
                                src={cancelIcon}
                                onClick={e =>
                                  toggleEditMode(
                                    e,
                                    folder.id,
                                    false,
                                    folder.name
                                  )
                                }
                              />
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="ws-folder-name-with-edit">
                        <span className="ws-folder-name">{folder.name}</span>
                        {!folder.isReference && folder.canRename && (
                          <span
                            className="ws-edit-name-icon"
                            data-toggle="tooltip"
                            data-placement="top"
                            data-container="body"
                            title="Edit name"
                            id={`edit-icon-${folder.id}`}
                            onMouseEnter={e =>
                              context?.showActionTooltip(
                                folder.id,
                                "Edit Name",
                                "edit"
                              )
                            }
                            onClick={e =>
                              toggleEditMode(e, folder.id, true, folder.name)
                            }
                          >
                            <img
                              src={editIcon}
                              alt="Edit folder name"
                              className="ws-edit-icon"
                            />
                          </span>
                        )}
                      </div>
                    )}
                    <div className="ws-folder-subtext">
                      {/* {folder.eventCount} folders */}
                    </div>
                  </div>
                </div>
                {context?.folderLoadingId === folder.id ? (
                  <LoadingSpinner />
                ) : (
                  <FolderActions folder={folder} />
                )}
              </div>
            </div>
          ))}
          {context?.canCreateRootContent && (
            <div
              className="ws-folder-card ws-folder-create"
              onClick={() => onClickCreateLibraryFolder()}
            >
              <div className="folder-create-icon-div">
                <div className="folder-create-icon">+</div>
              </div>
              <div className="ws-folder-create-text">Create Folder</div>
            </div>
          )}
        </div>
        {context?.rootFoldersNextPage && (
          <div className="ws-load-more-container ws-load-more-center">
            <button
              className="ws-load-more-folders-btn"
              onClick={() => context?.loadMoreRootFolders()}
              disabled={context?.foldersLoading}
            >
              {context?.foldersLoading ? (
                <>
                  <span className="fa fa-spinner fa-spin" style={{ marginRight: 5 }} />
                  Loading...
                </>
              ) : (
                "Load more folders"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryFoldersView;
